import { useEffect, useRef } from 'react';

/**
 * SEC-003 client-side mitigation: end the session after a period with no user
 * activity, so a token is not left usable on an unattended (often shared) device.
 *
 * Activity is shared across tabs through localStorage: an idle background tab
 * never logs out a tab the user is working in.
 */

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
export const LAST_ACTIVITY_KEY = 'BYOD-last-activity';

const ACTIVITY_WRITE_INTERVAL_MS = 30 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;
const WINDOW_ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'wheel', 'touchstart'];

export const recordActivity = (now: number = Date.now()): void => {
	try {
		localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
	} catch {
		// Storage unavailable: the server-side token expiry still applies.
	}
};

export const getLastActivity = (): number | null => {
	try {
		const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
		return Number.isFinite(value) && value > 0 ? value : null;
	} catch {
		return null;
	}
};

/** True when the last recorded activity (any tab) is older than the idle timeout. */
export const isIdleExpired = (now: number = Date.now()): boolean => {
	const last = getLastActivity();
	return last !== null && now - last > IDLE_TIMEOUT_MS;
};

export function useIdleLogout(active: boolean, onIdle: () => void): void {
	// Keep the latest callback without re-subscribing listeners on every render.
	const onIdleRef = useRef(onIdle);
	onIdleRef.current = onIdle;

	useEffect(() => {
		if (!active) return undefined;

		let lastWrite = 0;
		const onActivity = () => {
			const now = Date.now();
			if (now - lastWrite >= ACTIVITY_WRITE_INTERVAL_MS) {
				lastWrite = now;
				recordActivity(now);
			}
		};
		const onVisible = () => {
			if (document.visibilityState === 'visible') onActivity();
		};

		onActivity();
		WINDOW_ACTIVITY_EVENTS.forEach((event) =>
			window.addEventListener(event, onActivity, { passive: true })
		);
		document.addEventListener('visibilitychange', onVisible);

		const timer = window.setInterval(() => {
			if (isIdleExpired()) onIdleRef.current();
		}, IDLE_CHECK_INTERVAL_MS);

		return () => {
			WINDOW_ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
			document.removeEventListener('visibilitychange', onVisible);
			window.clearInterval(timer);
		};
	}, [active]);
}
